import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, Search, Receipt, Euro, Users, Briefcase, User, Building,
  CheckCircle2, XCircle, Clock, Loader2, Calendar, FileText, Filter,
  TrendingDown, CreditCard, Trash2, Edit, Eye, Download, ChevronRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const EXPENSE_TYPES = [
  { value: 'creative_payment', label: 'Paiement créatif', icon: User, color: 'bg-purple-100 text-purple-700' },
  { value: 'affiliate_payment', label: 'Paiement apporteur', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { value: 'project_expense', label: 'Dépense projet', icon: Briefcase, color: 'bg-orange-100 text-orange-700' },
  { value: 'agency_operating', label: 'Fonctionnement agence', icon: Building, color: 'bg-slate-100 text-slate-700' },
  { value: 'supplier', label: 'Fournisseur', icon: Receipt, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'other', label: 'Autre', icon: FileText, color: 'bg-gray-100 text-gray-700' }
];

const BILLING_TYPES = [
  { value: 'rebilled', label: 'Refacturée au client', color: 'bg-green-100 text-green-700' },
  { value: 'initial_budget', label: 'Budget initial projet', color: 'bg-blue-100 text-blue-700' },
  { value: 'internal', label: 'Interne (non refacturée)', color: 'bg-orange-100 text-orange-700' },
  { value: 'agency_operating', label: 'Fonctionnement agence', color: 'bg-slate-100 text-slate-700' }
];

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approuvée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rejetée', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-700', icon: XCircle }
};

const ExpensesPage = () => {
  const { token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [creatives, setCreatives] = useState([]);
  
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterBilling, setFilterBilling] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form
  const [form, setForm] = useState({
    title: '',
    description: '',
    amount: '',
    expense_type: 'project_expense',
    billing_type: 'internal',
    client_id: '',
    project_id: '',
    creative_id: '',
    supplier_name: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    category: '',
    notes: ''
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('expense_type', filterType);
      if (filterBilling !== 'all') params.append('billing_type', filterBilling);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const [expensesRes, statsRes, projectsRes, creativesRes] = await Promise.all([
        api.get(`/expenses/?${params.toString()}`),
        api.get('/expenses/stats'),
        api.get('/projects/'),
        api.get('/creatives/')
      ]);
      
      setExpenses(expensesRes.data.expenses || []);
      setStats(statsRes.data);
      setProjects(projectsRes.data.projects || projectsRes.data || []);
      setCreatives(creativesRes.data || []);
      
      // Fetch clients separately (users with role=client)
      try {
        const clientsRes = await api.get('/users/');
        const clientUsers = (clientsRes.data || []).filter(u => u.role === 'client');
        setClients(clientUsers);
      } catch (e) {
        console.log('Could not fetch clients');
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterBilling, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create expense
  const handleCreate = async () => {
    if (!form.title || !form.amount) {
      toast.error('Titre et montant requis');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        creative_id: form.creative_id || null
      };
      
      if (selectedExpense) {
        await api.put(`/expenses/${selectedExpense.id}`, payload);
        toast.success('Dépense mise à jour');
      } else {
        await api.post('/expenses/', payload);
        toast.success('Dépense créée');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleUpdateStatus = async (expenseId, status, paymentMethod = null) => {
    try {
      await api.put(`/expenses/${expenseId}/status`, { 
        status,
        payment_method: paymentMethod
      });
      toast.success('Statut mis à jour');
      fetchData();
      if (selectedExpense?.id === expenseId) {
        const res = await api.get(`/expenses/${expenseId}`);
        setSelectedExpense(res.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete expense
  const handleDelete = async (expenseId) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Dépense supprimée');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Edit expense
  const editExpense = (expense) => {
    setSelectedExpense(expense);
    setForm({
      title: expense.title || '',
      description: expense.description || '',
      amount: expense.amount?.toString() || '',
      expense_type: expense.expense_type || 'project_expense',
      billing_type: expense.billing_type || 'internal',
      client_id: expense.client_id || '',
      project_id: expense.project_id || '',
      creative_id: expense.creative_id || '',
      supplier_name: expense.supplier_name || '',
      invoice_number: expense.invoice_number || '',
      invoice_date: expense.invoice_date || '',
      due_date: expense.due_date || '',
      category: expense.category || '',
      notes: expense.notes || ''
    });
    setShowCreateModal(true);
  };

  // View expense detail
  const viewExpense = async (expense) => {
    try {
      const res = await api.get(`/expenses/${expense.id}`);
      setSelectedExpense(res.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching expense:', error);
    }
  };

  const resetForm = () => {
    setSelectedExpense(null);
    setForm({
      title: '',
      description: '',
      amount: '',
      expense_type: 'project_expense',
      billing_type: 'internal',
      client_id: '',
      project_id: '',
      creative_id: '',
      supplier_name: '',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      category: '',
      notes: ''
    });
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => 
    exp.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get type config
  const getTypeConfig = (type) => EXPENSE_TYPES.find(t => t.value === type) || EXPENSE_TYPES[5];
  
  // Get billing config
  const getBillingConfig = (type) => BILLING_TYPES.find(t => t.value === type) || BILLING_TYPES[2];

  // Format currency
  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(amount || 0);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <Header setMobileOpen={setMobileOpen} />

        <main className="p-4 md:p-6 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dépenses</h1>
              <p className="text-gray-500">Gérez les paiements créatifs, apporteurs et dépenses</p>
            </div>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="gap-2">
              <Plus className="w-4 h-4" />
              Nouvelle Dépense
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatCurrency(stats.totals?.pending)}</p>
                      <p className="text-xs text-gray-500">En attente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatCurrency(stats.totals?.paid)}</p>
                      <p className="text-xs text-gray-500">Payées</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {formatCurrency(stats.by_type?.creative_payment?.total || 0)}
                      </p>
                      <p className="text-xs text-gray-500">Créatifs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingDown className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.totals?.count || 0}</p>
                      <p className="text-xs text-gray-500">Total dépenses</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {EXPENSE_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterBilling} onValueChange={setFilterBilling}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Facturation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {BILLING_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="approved">Approuvée</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
                <SelectItem value="rejected">Rejetée</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Expenses List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">Aucune dépense enregistrée</p>
                <Button onClick={() => setShowCreateModal(true)} variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Créer une dépense
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto -mx-px">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-gray-600">Dépense</th>
                      <th className="text-left p-4 font-medium text-gray-600">Type</th>
                      <th className="text-left p-4 font-medium text-gray-600">Facturation</th>
                      <th className="text-left p-4 font-medium text-gray-600">Client / Projet</th>
                      <th className="text-right p-4 font-medium text-gray-600">Montant</th>
                      <th className="text-center p-4 font-medium text-gray-600">Statut</th>
                      <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredExpenses.map(expense => {
                      const typeConfig = getTypeConfig(expense.expense_type);
                      const billingConfig = getBillingConfig(expense.billing_type);
                      const statusConfig = STATUS_CONFIG[expense.status] || STATUS_CONFIG.pending;
                      const TypeIcon = typeConfig.icon;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className={cn("p-2 rounded-lg", typeConfig.color)}>
                                <TypeIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{expense.title}</p>
                                {expense.invoice_number && (
                                  <p className="text-xs text-gray-500">N° {expense.invoice_number}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className={billingConfig.color}>
                              {billingConfig.label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">
                              {expense.client && (
                                <p className="text-gray-900">{expense.client.first_name} {expense.client.last_name}</p>
                              )}
                              {expense.project && (
                                <p className="text-gray-500">{expense.project.name}</p>
                              )}
                              {expense.creative && (
                                <p className="text-purple-600">{expense.creative.name}</p>
                              )}
                              {!expense.client && !expense.project && !expense.creative && (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(expense.amount)}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <Badge className={cn("gap-1", statusConfig.color)}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => viewExpense(expense)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => editExpense(expense)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {expense.status === 'pending' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-600"
                                  onClick={() => handleUpdateStatus(expense.id, 'paid')}
                                >
                                  <CreditCard className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedExpense ? 'Modifier la dépense' : 'Nouvelle Dépense'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titre *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Paiement logo Julie Martin"
                />
              </div>
              
              <div>
                <Label>Montant (€) *</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="1500"
                />
              </div>
              
              <div>
                <Label>Type de dépense</Label>
                <Select value={form.expense_type} onValueChange={(val) => setForm({ ...form, expense_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type de facturation</Label>
                <Select value={form.billing_type} onValueChange={(val) => setForm({ ...form, billing_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Client (optionnel)</Label>
                <Select value={form.client_id || '_none'} onValueChange={(val) => setForm({ ...form, client_id: val === '_none' ? '' : val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucun</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} {c.company_name && `(${c.company_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Projet (optionnel)</Label>
                <Select value={form.project_id || '_none'} onValueChange={(val) => setForm({ ...form, project_id: val === '_none' ? '' : val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucun</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {(form.expense_type === 'creative_payment') && (
                <div className="col-span-2">
                  <Label>Créatif</Label>
                  <Select value={form.creative_id || '_none'} onValueChange={(val) => setForm({ ...form, creative_id: val === '_none' ? '' : val })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un créatif" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Aucun</SelectItem>
                      {creatives.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.user?.first_name} {c.user?.last_name} - {c.specialties?.join(', ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {(form.expense_type === 'supplier') && (
                <div className="col-span-2">
                  <Label>Nom du fournisseur</Label>
                  <Input
                    value={form.supplier_name}
                    onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                    placeholder="Nom du fournisseur"
                  />
                </div>
              )}
              
              <div>
                <Label>N° de facture</Label>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="FAC-2024-001"
                />
              </div>
              
              <div>
                <Label>Date de facture</Label>
                <Input
                  type="date"
                  value={form.invoice_date}
                  onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Catégorie</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Ex: Design, Vidéo, Hébergement..."
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Description de la dépense..."
                  rows={2}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Notes internes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notes internes..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedExpense ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          {selectedExpense && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {selectedExpense.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex justify-between items-center">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(selectedExpense.amount)}
                  </span>
                  <Badge className={STATUS_CONFIG[selectedExpense.status]?.color}>
                    {STATUS_CONFIG[selectedExpense.status]?.label}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Type</p>
                    <p className="font-medium">{getTypeConfig(selectedExpense.expense_type).label}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Facturation</p>
                    <p className="font-medium">{getBillingConfig(selectedExpense.billing_type).label}</p>
                  </div>
                  {selectedExpense.client && (
                    <div>
                      <p className="text-gray-500">Client</p>
                      <p className="font-medium">{selectedExpense.client.first_name} {selectedExpense.client.last_name}</p>
                    </div>
                  )}
                  {selectedExpense.project && (
                    <div>
                      <p className="text-gray-500">Projet</p>
                      <p className="font-medium">{selectedExpense.project.name}</p>
                    </div>
                  )}
                  {selectedExpense.creative && (
                    <div className="col-span-2">
                      <p className="text-gray-500">Créatif</p>
                      <p className="font-medium text-purple-600">{selectedExpense.creative.name}</p>
                    </div>
                  )}
                  {selectedExpense.invoice_number && (
                    <div>
                      <p className="text-gray-500">N° Facture</p>
                      <p className="font-medium">{selectedExpense.invoice_number}</p>
                    </div>
                  )}
                  {selectedExpense.due_date && (
                    <div>
                      <p className="text-gray-500">Échéance</p>
                      <p className="font-medium">{new Date(selectedExpense.due_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                </div>
                
                {selectedExpense.description && (
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Description</p>
                    <p className="text-gray-700">{selectedExpense.description}</p>
                  </div>
                )}
                
                {selectedExpense.notes && (
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-yellow-800 text-sm">{selectedExpense.notes}</p>
                  </div>
                )}
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => handleDelete(selectedExpense.id)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <Button variant="outline" onClick={() => { setShowDetailModal(false); editExpense(selectedExpense); }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                {selectedExpense.status === 'pending' && (
                  <>
                    <Button variant="outline" onClick={() => handleUpdateStatus(selectedExpense.id, 'approved')}>
                      Approuver
                    </Button>
                    <Button onClick={() => handleUpdateStatus(selectedExpense.id, 'paid')}>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Marquer payée
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
